import { useEffect, useRef, useState } from 'react'
import ReactCountryFlag from 'react-country-flag'
import { useTranslation } from 'react-i18next'

import { Button, Text } from '@shared-ui/components'
import {
    ChevronDropdownIcon,
    ChevronDropdownUpIcon,
} from '@shared-ui/svg/index'
import { cn } from '@shared-ui/utils'

export const LanguageSwitcher = ({
    isMobile = false,
}: {
    isMobile?: boolean
}) => {
    const { i18n } = useTranslation()
    const [isOpen, setIsOpen] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)

    const languages = [
        { code: 'hr', countryCode: 'HR', label: 'Hrvatski', id: 1 },
        { code: 'en-GB', countryCode: 'GB', label: 'English', id: 2 },
        { code: 'de', countryCode: 'DE', label: 'Deutsch', id: 3 },
        { code: 'fr', countryCode: 'FR', label: 'Français', id: 4 },
        { code: 'it', countryCode: 'IT', label: 'Italiano', id: 5 },
        { code: 'es', countryCode: 'ES', label: 'Español', id: 6 },
        { code: 'pt', countryCode: 'PT', label: 'Português', id: 7 },
    ]

    const handleLanguageChange = (languageCode: string) => {
        i18n.changeLanguage(languageCode)
        localStorage.setItem('preferredLanguage', languageCode)
        setIsOpen(false)
    }

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node | null)
            ) {
                setIsOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () =>
            document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const currentLanguage =
        languages.find((lang) => lang.code === i18n.language) || languages[0]

    return (
        <div
            className={cn(
                'relative inline-block items-center gap-2 rounded-md border border-none bg-background-base px-1 py-2 text-left text-sm font-medium text-color-title',
                isMobile && 'w-full'
            )}
            ref={dropdownRef}
        >
            <div
                className={cn(
                    'flex w-full cursor-pointer items-center',
                    isMobile && 'w-full justify-around gap-2'
                )}
                onClick={() => setIsOpen(!isOpen)}
            >
                <ReactCountryFlag
                    countryCode={currentLanguage.countryCode}
                    svg
                    style={{
                        width: '2.5em',
                        height: '1.5em',
                        borderRadius: '12px',
                    }}
                />
                {isMobile ? (
                    <Text
                        text={currentLanguage.label}
                        variant="text"
                        size="sm"
                        color="title"
                    />
                ) : null}
                <Button
                    label=""
                    iconAfter={
                        isOpen ? (
                            <ChevronDropdownIcon />
                        ) : (
                            <ChevronDropdownUpIcon />
                        )
                    }
                    variant="text"
                    shape="rounded"
                    color="title"
                    size="lg"
                    className="accordionBtn h-[40px] w-[40px] shrink-0 shadow-none"
                    onClick={() => setIsOpen(!isOpen)}
                />
            </div>

            {isOpen && (
                <div
                    className={cn(
                        'absolute right-0 z-30 mt-2 w-40 origin-top-right rounded-md bg-background-base shadow-modal',
                        isMobile && 'w-full'
                    )}
                >
                    <div className="mx-auto w-full items-center justify-center">
                        {languages.map((lang) => (
                            <button
                                key={lang.id}
                                onClick={() => handleLanguageChange(lang.code)}
                                className="flex w-full items-center justify-start gap-2 px-6 py-2.5 text-sm text-gray-700 hover:bg-background-disabled/20"
                            >
                                <ReactCountryFlag
                                    countryCode={lang.countryCode}
                                    svg
                                    style={{
                                        width: '2.5em',
                                        height: '1.5em',
                                        borderRadius: '12px',
                                    }}
                                    aria-label={lang.label}
                                />
                                <Text
                                    text={lang.label}
                                    variant="text"
                                    size="sm"
                                    color="title"
                                />
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}
